import {faInfoCircle} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Alert, Button, Form, FormInstance, Modal, Radio, RadioChangeEvent, Spin} from 'antd';
import {useEffect, useMemo, useState} from 'react';

import {useDomainConfig} from '../../../../provider/domain-config/domain-config-hook';
import {Locale} from '../../../../provider/locale/locale';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {useModal} from '../../../../provider/modal/modal-hook';
import {
    useDeleteExportTemplateMutation,
    useExportConstructorExportMutation,
    useExportTemplates,
    useLegacyReviewsExportMutation,
} from '../../../../service/reviews/export-constructor/export-constructor-hook';
import {ExportTemplateType} from '../../../../service/reviews/export-constructor/export-constructor-type';
import {classNames} from '../../../../util/css';
import {useReviewStateHook} from '../reviews-state/reviews-state-provider';
import {ReviewsStateType} from '../reviews-state/reviews-state-type';

import {ExportTemplateListItem} from './components/export-template-list-item/export-template-list-item';
import {
    appendLegacyFullExportOldTemplate,
    buildExportConstructorExportPayload,
    buildLegacyExportReportOptions,
    commentsLayoutStorageKey,
    exportConstructorListFormId,
    getLocalSystemExportTemplates,
    getSystemTemplateCode,
    getSystemTemplateSheetType,
    isCatalogRatingsTemplate,
    isLegacyFullExportOldTemplate,
    isLocalSystemExportTemplate,
    sortExportTemplates,
    validateExportConstructorEmails,
} from './reviews-export-constructor-helper';
import {
    ExportConstructorEditorStateType,
    ExportConstructorListFormEnum,
    ExportConstructorListFormType,
} from './reviews-export-constructor-types';
import * as styles from './reviews-export-constructor-modal.module.scss';

type PropsType = {
    className?: string;
    hasReviews: boolean;
    isOpen: boolean;
    mainFilterKey: string;
    onClose: () => void;
    onOpenEditor: (editorState: ExportConstructorEditorStateType) => void;
    reviewsState: ReviewsStateType;
};

// eslint-disable-next-line max-statements
export function ReviewsExportConstructorModal(props: PropsType): JSX.Element {
    const {className, hasReviews, isOpen, mainFilterKey, onClose, onOpenEditor, reviewsState} = props;

    const {domainName} = useDomainConfig();
    const {getLocalizedString} = useLocale();
    const {modal} = useModal();

    const [form]: [FormInstance<ExportConstructorListFormType>] = Form.useForm();
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [templateEmails, setTemplateEmails] = useState<Record<number, string>>({});

    const {data: apiTemplates, isError, isFetched, isLoading} = useExportTemplates(isOpen);

    const hasApiTemplates = Boolean(apiTemplates?.length);
    const isLegacyExportMode = isFetched && (isError || !hasApiTemplates);
    const isCustomExportAvailable = isFetched && !isError && hasApiTemplates;

    const templates = useMemo(() => {
        if (!isFetched) {
            return [];
        }

        if (isLegacyExportMode) {
            return getLocalSystemExportTemplates(domainName);
        }

        return appendLegacyFullExportOldTemplate(apiTemplates ?? []);
    }, [apiTemplates, domainName, isFetched, isLegacyExportMode]);

    const {mutate: deleteTemplate, isLoading: isDeleting} = useDeleteExportTemplateMutation();
    const {mutate: exportReport, isLoading: isExporting} = useExportConstructorExportMutation();
    const {mutate: legacyExportReport, isLoading: isLegacyExporting} = useLegacyReviewsExportMutation();
    const {countFilterFieldDifferences} = useReviewStateHook();

    const sortedTemplates = useMemo(() => {
        if (isLegacyExportMode) {
            return templates;
        }

        return sortExportTemplates(templates, domainName);
    }, [domainName, isLegacyExportMode, templates]);

    const selectedTemplate = sortedTemplates.find((template) => template.id === selectedTemplateId) ?? null;
    const isDownloadDisabled = !selectedTemplate || (!hasReviews && !isCatalogRatingsTemplate(selectedTemplate));

    const hasAppliedFilters = countFilterFieldDifferences > 0;

    useEffect(() => {
        if (isOpen) {
            setSelectedTemplateId(null);
            setTemplateEmails({});
            // eslint-disable-next-line no-restricted-globals
            const saved = localStorage.getItem(commentsLayoutStorageKey) as
                | ExportConstructorListFormType['commentsLayout']
                | null;

            form.resetFields();
            form.setFieldsValue({
                [ExportConstructorListFormEnum.CommentsLayout]: saved ?? 'rows',
                [ExportConstructorListFormEnum.WithReviewsSummary]: false,
                [ExportConstructorListFormEnum.WithTagsStat]: false,
                [ExportConstructorListFormEnum.Name]: '',
                [ExportConstructorListFormEnum.AdditionalUserEmails]: '',
            });
        }
    }, [form, isOpen]);

    function handleTemplateChange(radioEvent: RadioChangeEvent) {
        const templateId = Number(radioEvent.target.value);
        const currentEmail = form.getFieldValue(ExportConstructorListFormEnum.AdditionalUserEmails) ?? '';

        const updatedEmails =
            selectedTemplateId !== null ? {...templateEmails, [selectedTemplateId]: currentEmail} : templateEmails;

        setTemplateEmails(updatedEmails);
        setSelectedTemplateId(templateId);

        form.setFieldValue(ExportConstructorListFormEnum.Name, '');

        form.setFieldValue(ExportConstructorListFormEnum.AdditionalUserEmails, updatedEmails[templateId] ?? '');
    }

    function handleOpenEditor(editorState: ExportConstructorEditorStateType) {
        if (!isCustomExportAvailable) {
            return;
        }

        onOpenEditor(editorState);
    }

    function handleCloneTemplate(template: ExportTemplateType) {
        if (isLegacyFullExportOldTemplate(template)) {
            return;
        }

        const systemCode = getSystemTemplateCode(template);

        if (isLocalSystemExportTemplate(template)) {
            handleOpenEditor({
                mode: 'clone',
                sourceTemplate: template,
                ...(systemCode ? {initialSheetType: getSystemTemplateSheetType(systemCode)} : {}),
            });

            return;
        }

        handleOpenEditor({
            mode: 'clone',
            sourceTemplateId: template.id,
            ...(systemCode ? {initialSheetType: getSystemTemplateSheetType(systemCode)} : {}),
        });
    }

    function handleDelete(template: ExportTemplateType) {
        modal.confirm({
            maskClosable: true,
            title: <Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__DELETE__TITLE" />,
            content: (
                <Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__DELETE__DESCRIPTION" valueMap={{name: template.name}} />
            ),
            okText: <Locale stringKey="BUTTON__DELETE" />,
            cancelText: <Locale stringKey="POPUP__BUTTON__CANCEL" />,
            okButtonProps: {danger: true, loading: isDeleting},
            onOk: () => {
                deleteTemplate(template.id, {
                    onSuccess: () => {
                        setSelectedTemplateId((currentId) => (currentId === template.id ? null : currentId));
                    },
                });
            },
        });
    }

    function handleSubmit(formValues: ExportConstructorListFormType) {
        if (!selectedTemplate) {
            return;
        }

        const emailValidationError = validateExportConstructorEmails(formValues.additionalUserEmails ?? '');

        if (emailValidationError) {
            form.setFields([
                {
                    name: ExportConstructorListFormEnum.AdditionalUserEmails,
                    errors: [getLocalizedString(emailValidationError.errorKey, emailValidationError.valueMap)],
                },
            ]);

            return;
        }

        if (
            isLegacyExportMode ||
            isLocalSystemExportTemplate(selectedTemplate) ||
            isLegacyFullExportOldTemplate(selectedTemplate)
        ) {
            const legacyOptions = buildLegacyExportReportOptions(formValues, selectedTemplate);

            if (!legacyOptions) {
                return;
            }

            legacyExportReport(
                {
                    options: legacyOptions,
                    reviewsState,
                    mainFilterKey,
                },
                {onSuccess: onClose}
            );

            return;
        }

        exportReport(
            {
                payload: buildExportConstructorExportPayload(formValues, selectedTemplate),
                reviewsState,
                mainFilterKey,
            },
            {onSuccess: onClose}
        );
    }

    return (
        <Modal
            className={classNames(styles.ReviewsExportConstructorModal, className)}
            footer={[
                <Button className={styles.ReviewsExportConstructorModal_footerButton} key="close" onClick={onClose}>
                    <Locale stringKey="POPUP__BUTTON__CANCEL" />
                </Button>,
                <Button
                    className={styles.ReviewsExportConstructorModal_footerButton}
                    disabled={isDownloadDisabled}
                    form={exportConstructorListFormId}
                    htmlType="submit"
                    key="submit"
                    loading={isExporting || isLegacyExporting}
                    type="primary"
                >
                    <Locale stringKey="REVIEWS__DOWNLOAD_REPORT_POPUP__BUTTON__REQUEST" />
                </Button>,
            ]}
            onCancel={onClose}
            open={isOpen}
            title={<Locale stringKey="REVIEWS__DOWNLOAD_REPORT_POPUP__HEADER" />}
            width="630px"
        >
            <Spin spinning={isLoading}>
                <Form<ExportConstructorListFormType>
                    className={styles.ReviewsExportConstructorModal_form}
                    form={form}
                    id={exportConstructorListFormId}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    {hasAppliedFilters ? (
                        <Alert
                            className={styles.ReviewsExportConstructorModal_info}
                            icon={
                                <FontAwesomeIcon
                                    className={styles.ReviewsExportConstructorModal_infoIcon}
                                    icon={faInfoCircle}
                                />
                            }
                            message={<Locale stringKey="REVIEWS__DOWNLOAD_REPORT_POPUP__INFO" />}
                            showIcon
                            type="info"
                        />
                    ) : null}

                    <div className={styles.ReviewsExportConstructorModal_list}>
                        <Radio.Group
                            className={styles.ReviewsExportConstructorModal_radioGroup}
                            onChange={handleTemplateChange}
                            value={selectedTemplateId}
                        >
                            {sortedTemplates.map((template) => (
                                <ExportTemplateListItem
                                    isCustomExportAvailable={isCustomExportAvailable}
                                    isDownloadDisabled={isDownloadDisabled}
                                    isSelected={selectedTemplateId === template.id}
                                    key={template.id}
                                    onClone={handleCloneTemplate}
                                    onDelete={handleDelete}
                                    onEdit={(templateId) => {
                                        handleOpenEditor({mode: 'edit', templateId});
                                    }}
                                    template={template}
                                />
                            ))}
                        </Radio.Group>
                    </div>
                </Form>
            </Spin>
        </Modal>
    );
}
